import React, { useRef, useState, useEffect, useCallback, memo } from 'react';

function useLazy(rootMargin = '600px', eager = false) {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(eager);

  useEffect(() => {
    if (eager) {
      setReady(true);
      return;
    }
    if (!window.IntersectionObserver) {
      setReady(true);
      return;
    }
    
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setReady(true);
          obs.disconnect();
        }
      },
      { rootMargin, threshold: 0 }
    );
    obs.observe(el);
    
    const fallbackTimer = setTimeout(() => {
       setReady(true);
       obs.disconnect();
    }, 1500);

    return () => {
       obs.disconnect();
       clearTimeout(fallbackTimer);
    };
  }, [rootMargin, eager]);

  return { ref, ready };
}

type LazyVideoProps = React.VideoHTMLAttributes<HTMLVideoElement> & {
  rootMargin?: string;
  /** Load immediately (e.g. first hero slides). */
  eager?: boolean;
};

export const LazyVideo = memo<LazyVideoProps>(({ rootMargin, eager, className, muted, style, ...props }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { ref, ready } = useLazy(rootMargin, !!eager);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const tryPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
    } catch { /* ignore */ }
    if (muted !== false) v.muted = true;
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  }, [muted]);

  useEffect(() => {
    if (!ready || !videoRef.current) return;
    tryPlay();
  }, [ready, tryPlay, props.src]);

  if (!ready) {
    return (
      <div
        ref={ref}
        className={className}
        style={{ minHeight: '100%', background: '#0a0a0a', ...style }}
        aria-hidden
      />
    );
  }

  if (hasError) {
    return (
      <div
        className={className}
        style={{ minHeight: '100%', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}
      >
        <span style={{ color: '#555', fontSize: '10px', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.1em' }}>video</span>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      playsInline
      preload="auto"
      {...props}
      style={style}
      className={className}
      muted={muted !== false ? true : muted}
      onError={() => setHasError(true)}
      onCanPlay={() => { if (!isPlaying) tryPlay(); }}
    />
  );
});

LazyVideo.displayName = 'LazyVideo';

type LazyIframeProps = React.IframeHTMLAttributes<HTMLIFrameElement> & {
  rootMargin?: string;
  eager?: boolean;
};

export const LazyIframe = memo<LazyIframeProps>(({ rootMargin, eager, ...props }) => {
  const { ref, ready } = useLazy(rootMargin, !!eager);
  if (ready) {
    const allow =
      props.allow ||
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    return <iframe {...props} allow={allow} />;
  }
  return <div ref={ref} className={props.className} style={{ minHeight: '100%', background: '#0a0a0a' }} aria-hidden />;
});

LazyIframe.displayName = 'LazyIframe';
